import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Form, Input, Select, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { ResourceHistory } from '@/components/resource-history';
import { API } from '@/services/api.service';
import { errorText } from '../../_components/format';

const LIMITS = { contactPerson: 120, email: 200, phone: 50, note: 4000 };

/** Label / value rows, empty values left out. */
function Rows({ rows }) {
  const shown = rows.filter(([, v]) => v);
  return (
    <dl className="m-0 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-[13px]">
      {shown.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="font-semibold text-zinc-500">{label}</dt>
          <dd className="m-0 min-w-0 whitespace-pre-line break-words">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Block({ title, hint, action, children }) {
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="m-0 text-[14px] font-bold">{title}</h3>
        {action}
      </div>
      {hint && <p className="mb-2 mt-0 text-[12px] text-zinc-500">{hint}</p>}
      {children}
    </section>
  );
}

/**
 * The "Data" tab: what the invoices say about the supplier, what the books
 * hold (only under the ledger switch; read-only), and the contact block kept
 * on the platform only — editable by everyone who sees the supplier, its
 * changes in the history next to it.
 */
export default function SupplierData({ company, supplier }) {
  const { t } = useTranslation('suppliers');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();
  const note = supplier.note || {};
  // The company's people, asked for only once the block is being edited.
  const { data: people = [], isLoading: peopleLoading } = useQuery({
    queryKey: ['suppliers', company, 'people'],
    queryFn: () => API.suppliers.getPeople(company),
    enabled: editing,
    staleTime: 5 * 60 * 1000,
  });

  const save = useMutation({
    mutationFn: (values) => API.suppliers.saveNote(company, supplier.pib, values),
    onSuccess: () => {
      message.success(t('data.saved'));
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['suppliers', company, 'profile', supplier.pib] });
    },
    onError: (error) => message.error(errorText(t, error)),
  });

  const field = (name, extra = {}) => ({
    name,
    label: t(`data.fields.${name}`),
    rules: [{ max: LIMITS[name], message: t('data.tooLong', { max: LIMITS[name] }) }, ...(extra.rules || [])],
  });

  return (
    <div className="grid gap-3">
      <Block title={t('data.fromInvoices')}>
        <Rows
          rows={[
            [t('data.fields.name'), supplier.name],
            [t('columns.pib'), supplier.pib],
            [t('columns.registrationNo'), supplier.registrationNo],
            [t('data.fields.address'), supplier.address],
            [t('data.fields.email'), supplier.email],
            [t('data.fields.bankAccounts'), supplier.bankAccounts.join('\n')],
            [t('data.fields.invoiceCount'), String(supplier.invoiceCount)],
          ]}
        />
      </Block>

      {supplier.ledger && (
        <Block title={t('data.fromBooks')} hint={t('data.fromBooksHint')}>
          {supplier.books.length ? (
            supplier.books.map((b) => (
              <Rows
                key={b.code}
                rows={[
                  [t('data.fields.code'), b.code],
                  [t('data.fields.name'), b.name],
                  [t('data.fields.address'), b.address],
                  [t('columns.pib'), b.pib],
                  [t('columns.registrationNo'), b.registrationNo],
                  [t('data.fields.bankAccount'), b.bankAccount],
                  [t('data.fields.email'), b.email],
                ]}
              />
            ))
          ) : (
            <p className="m-0 text-[13px] text-zinc-500">{t('data.notInBooks')}</p>
          )}
        </Block>
      )}

      <Block
        title={t('data.contactTitle')}
        hint={t('data.contactHint')}
        action={
          !editing && (
            <Button
              shape="round"
              icon={<EditOutlined />}
              onClick={() => {
                form.setFieldsValue({ ...note, followedBy: note.followedBy?._id ? String(note.followedBy._id) : undefined });
                setEditing(true);
              }}
            >
              {t('data.edit')}
            </Button>
          )
        }
      >
        {editing ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => save.mutate({ ...values, followedBy: values.followedBy || null })}
            className="max-w-[640px]"
          >
            <Form.Item {...field('contactPerson')}>
              <Input />
            </Form.Item>
            <Form.Item {...field('email', { rules: [{ type: 'email', message: t('data.badEmail') }] })}>
              <Input />
            </Form.Item>
            <Form.Item {...field('phone')}>
              <Input />
            </Form.Item>
            <Form.Item name="followedBy" label={t('data.fields.followedBy')} tooltip={t('data.followedByHint')}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                loading={peopleLoading}
                placeholder={t('data.pickPerson')}
                options={people.map((p) => ({ value: String(p._id), label: p.email ? `${p.name} · ${p.email}` : p.name }))}
              />
            </Form.Item>
            <Form.Item {...field('note')}>
              <Input.TextArea autoSize={{ minRows: 3, maxRows: 12 }} showCount maxLength={LIMITS.note} />
            </Form.Item>
            <div className="flex justify-end gap-2">
              <Button shape="round" onClick={() => setEditing(false)} disabled={save.isPending}>
                {t('data.cancel')}
              </Button>
              <Button shape="round" type="primary" htmlType="submit" loading={save.isPending}>
                {t('data.save')}
              </Button>
            </div>
          </Form>
        ) : (
          <>
            <Rows
              rows={[
                [t('data.fields.contactPerson'), note.contactPerson],
                [t('data.fields.email'), note.email],
                [t('data.fields.phone'), note.phone],
                [t('data.fields.followedBy'), note.followedBy?.name],
                [t('data.fields.note'), note.note],
              ]}
            />
            {!note.contactPerson && !note.email && !note.phone && !note.followedBy && !note.note && (
              <p className="m-0 text-[13px] text-zinc-500">{t('data.contactEmpty')}</p>
            )}
            {note.updatedAt && (
              <p className="mb-0 mt-2 text-[12px] text-zinc-500">
                {t('data.updated', { name: note.updatedBy?.name || '—', date: new Date(note.updatedAt).toLocaleString('sr-RS') })}
              </p>
            )}
          </>
        )}
      </Block>

      {note._id && <ResourceHistory modelName="SupplierNote" documentId={note._id} variant="profile" />}
    </div>
  );
}
